import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import {
  BULK_OIL_REFERENCE_MINIMAL_QUANTITY_LIMIT,
  CERTAS_OIL_REFERENCE_QUANTITY_LIMIT,
  REFERENCE_MAX_QUANTITY_LIMIT,
} from 'domains/basket/Basket.types';
import { DeleteAltButton, Flex, MarginBox, MinusButton, PlusButton } from 'UI';
import { ONCHANGE_DEBOUNCE_INTERVAL } from 'utils/hooks/useInput';
import { QtyButton, QtyInput, QtyInputPill, QtyPillButton, QtyPillWrapper } from './QuantityModule.styled';

export type QuantityModuleUpdateComponentType = 'TRASH' | 'BUTTON' | 'INPUT';
export type QuantityModuleDesign = 'default' | 'v2';
export const minBulkChunkValue = 1;

export const getSingleUnitIncrementDisabledTooltip = (
  t: (key: string, fallback: string) => string,
  isSingleUnitRestricted: boolean,
) =>
  isSingleUnitRestricted
    ? t(
        'cart.action.add_reference.traction_battery_single_unit_tooltip',
        'This part is restricted to single-unit orders',
      )
    : undefined;

interface QuantityModuleProps {
  value: number;
  onChange: (val: number, type?: QuantityModuleUpdateComponentType) => void;
  showDelete?: boolean;
  precisionMin?: number;
  precisionMax?: number;
  noDebounce?: boolean;
  disabled?: boolean;
  maxValue?: number;
  onMaxValueOverflow?: () => void;
  chunk?: number;
  isMinBulkChunk?: boolean;
  size?: 'small' | 'middle' | 'large';
  noFixedWidth?: boolean;
  isCertasOilReference?: boolean;
  isSingleUnitRestricted?: boolean;
  design?: QuantityModuleDesign;
}

const getMaxValue = (isCertasOilReference: boolean, maxValue: number | undefined) => {
  return maxValue ?? (isCertasOilReference ? CERTAS_OIL_REFERENCE_QUANTITY_LIMIT : REFERENCE_MAX_QUANTITY_LIMIT);
};

const shouldShowDeleteButton = (inputValue: number, chunk: number, isMinBulkChunk: boolean, showDelete = false) =>
  Boolean(showDelete) &&
  (inputValue <= chunk || (isMinBulkChunk && inputValue <= BULK_OIL_REFERENCE_MINIMAL_QUANTITY_LIMIT));

function QuantityModuleLocal({
  value,
  onChange,
  showDelete,
  precisionMin,
  precisionMax,
  noDebounce = false,
  disabled = false,
  maxValue,
  onMaxValueOverflow,
  chunk = 1,
  isMinBulkChunk = false,
  size = 'middle',
  noFixedWidth = false,
  isCertasOilReference = false,
  isSingleUnitRestricted = false,
  design = 'default',
}: Readonly<QuantityModuleProps>) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);
  const [inputKey, setInputKey] = useState(0);
  const debouncedOnChange = useDebouncedCallback(onChange, ONCHANGE_DEBOUNCE_INTERVAL);
  const [maxValueLocal, setMaxValueLocal] = useState(getMaxValue(isCertasOilReference, maxValue));
  const [isInteracting, setIsInteracting] = useState(false); // Track if the user is interacting
  const timeoutRef = useRef<NodeJS.Timeout | null>(null); // Reference to the timeout

  const getChunk = () => (isMinBulkChunk ? minBulkChunkValue : chunk);

  const clearUpdateTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Schedule the update after user interaction
  const scheduleUpdate = useCallback(() => {
    clearUpdateTimeout(); // Clear any previous timeout

    timeoutRef.current = setTimeout(() => {
      if (!isInteracting) {
        setInputValue(value);
      }
      setIsInteracting(false);
    }, 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!isInteracting) {
      setInputValue(value);
    } else {
      scheduleUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInteracting]);

  // Sync maxValueLocal when the maxValue prop changes (e.g., stock loads after the component mounts)
  useEffect(() => {
    setMaxValueLocal(getMaxValue(isCertasOilReference, maxValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxValue, isCertasOilReference]);

  // Handle immediate updates from external value changes
  useEffect(() => {
    const isMaxValueOverflow = value > maxValueLocal;
    const clampedValue = isMaxValueOverflow ? maxValueLocal : value;

    if (!isInteracting) {
      setInputValue(clampedValue);
    } else {
      scheduleUpdate();
    }

    isMaxValueOverflow && setInputValue(clampedValue);
    if (isMaxValueOverflow && onMaxValueOverflow) {
      onMaxValueOverflow();
    }

    if (inputValue === value + getChunk()) {
      setMaxValueLocal(value);
    }

    setInputKey((prev) => prev + 1);
    return () => clearUpdateTimeout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, maxValueLocal, onMaxValueOverflow]);

  const handleChange = useCallback(
    (val: number, type?: QuantityModuleUpdateComponentType, noDebounceLocal = false) => {
      setInputValue(val);
      noDebounce || noDebounceLocal ? onChange(val, type) : debouncedOnChange(val, type);
      setIsInteracting(true);
      scheduleUpdate();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [noDebounce, onChange, debouncedOnChange],
  );

  const handleClickDeleteButton = () => {
    const call = showDelete ? inputValue > 0 : inputValue > 1;
    call && handleChange(0, 'TRASH', true);
  };

  const handleClickMinusButton = () => {
    const call = showDelete ? inputValue > 0 : inputValue > 1;
    call && handleChange(inputValue - getChunk(), 'BUTTON', false);
  };

  const handleClickPlusButton = () => {
    inputValue + getChunk() <= maxValueLocal && handleChange(inputValue + getChunk(), 'BUTTON', false);
  };

  const isIncrementBlocked = disabled || inputValue + getChunk() > maxValueLocal;
  const incrementDisabledTooltip = getSingleUnitIncrementDisabledTooltip(t, isSingleUnitRestricted);

  const showDeleteButton = shouldShowDeleteButton(inputValue, getChunk(), isMinBulkChunk, showDelete);

  const isV2 = design === 'v2';
  const v2ButtonColors = isV2 ? ({ color: 'brand_black', bgColor: 'transparent' } as const) : {};

  // Widen the input when decimals are allowed so values like "10.000" fit (≈8px per decimal digit + dot).
  const decimalExtraWidth = precisionMax && precisionMax > 0 ? precisionMax * 8 + 6 : 0;

  const leftButtonDisabled = disabled || showDelete ? false : inputValue === getChunk();

  const leftButton = showDeleteButton ? (
    <DeleteAltButton
      onClick={handleClickDeleteButton}
      disabled={leftButtonDisabled}
      {...v2ButtonColors}
      data-cy={'delete-quantity-button'}
    />
  ) : (
    <MinusButton
      onClick={handleClickMinusButton}
      disabled={leftButtonDisabled}
      {...v2ButtonColors}
      data-cy={'decrease-quantity-button'}
    />
  );

  const rightButton = (
    <PlusButton
      color={'brand_black'}
      onClick={handleClickPlusButton}
      disabled={isIncrementBlocked}
      tooltip={isIncrementBlocked ? incrementDisabledTooltip : undefined}
      {...(isV2 ? ({ bgColor: 'transparent' } as const) : {})}
      data-cy={'increase-quantity-button'}
    />
  );

  const handleOnChangeDebounced = (val: number | undefined) => {
    if (val === undefined || val === inputValue) {
      return;
    }
    const newValue =
      precisionMax && precisionMax > 0
        ? calculateRoundedValue(val, precisionMax)
        : calculateChunkedValue(val, getChunk());

    if (newValue === inputValue) {
      setInputKey((prev) => prev + 1);
    }
    handleChange(newValue, 'INPUT', true);

    // Mark as no longer interacting (if blur happens)
    setIsInteracting(false);
    clearUpdateTimeout(); // Clear timeout when the user blurs the input
  };

  const calculateRoundedValue = (val: number, precision: number): number => {
    const decimalFactor = Math.pow(10, precision);
    return Math.round(val * decimalFactor) / decimalFactor;
  };

  const calculateChunkedValue = (val: number, chunkModifier: number): number =>
    Math.floor(val / chunkModifier) * chunkModifier || chunkModifier;

  // Cleanup the timeout on component unmount
  useEffect(() => {
    return () => {
      clearUpdateTimeout(); // Clear the timeout when the component unmounts
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (design === 'v2') {
    return (
      <QtyPillWrapper $size={size}>
        <QtyPillButton $size={size} $position={'left'} disabled={leftButtonDisabled}>
          {leftButton}
        </QtyPillButton>
        <QtyInputPill
          key={`input-key-${inputKey}`}
          value={inputValue}
          bordered
          precisionMin={precisionMin}
          precisionMax={precisionMax}
          min={isMinBulkChunk ? BULK_OIL_REFERENCE_MINIMAL_QUANTITY_LIMIT : 0}
          max={maxValueLocal}
          onChangeDebounced={handleOnChangeDebounced}
          readOnly={disabled}
          noFixedWidth={noFixedWidth}
          data-cy={'quantity-input'}
          digitsOnly
          $size={size}
          $extraWidth={decimalExtraWidth}
        />
        <QtyPillButton $size={size} $position={'right'} disabled={isIncrementBlocked}>
          {rightButton}
        </QtyPillButton>
      </QtyPillWrapper>
    );
  }

  return (
    <Flex
      size={0}
      minWidth={size === 'large' ? 250 : undefined}
      minHeight={size === 'small' ? 30 : 50}
      align={'center'}
      justify={size === 'large' ? 'center' : undefined}
    >
      <QtyButton $size={size}>{leftButton}</QtyButton>
      <QtyInput
        key={`input-key-${inputKey}`}
        value={inputValue}
        bordered
        precisionMin={precisionMin}
        precisionMax={precisionMax}
        min={isMinBulkChunk ? BULK_OIL_REFERENCE_MINIMAL_QUANTITY_LIMIT : 0}
        max={maxValueLocal}
        onChangeDebounced={handleOnChangeDebounced}
        readOnly={disabled}
        noFixedWidth={noFixedWidth}
        data-cy={'quantity-input'}
        digitsOnly
        $size={size}
        $extraWidth={decimalExtraWidth}
      />
      <MarginBox ml={size === 'small' ? 4 : 10}>
        <QtyButton $size={size}>{rightButton}</QtyButton>
      </MarginBox>
    </Flex>
  );
}

export const QuantityModule = React.memo(QuantityModuleLocal);
